import { SEO } from "@/components/SEO";

import { useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Package, Layers, Printer, PenTool, Scissors, Truck } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

function AnimatedDiv({ children, className = "" }) {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp} className={className}>
      {children}
    </motion.div>
  );
}

const steps = [
  { title: "Fabric Sourcing", desc: "Carefully selected raw materials from trusted suppliers ensuring quality from the foundation.", icon: Package, num: "01" },
  { title: "Processing", desc: "Advanced dyeing and processing techniques for consistent color fastness and fabric quality.", icon: Layers, num: "02" },
  { title: "Printing", desc: "State-of-the-art digital and rotary printing for vibrant, detailed patterns.", icon: Printer, num: "03" },
  { title: "Embroidery", desc: "Precision embroidery work adding intricate detail and value to fabrics.", icon: PenTool, num: "04" },
  { title: "Stitching", desc: "Expert garment construction with attention to fit, finish, and durability.", icon: Scissors, num: "05" },
  { title: "Finishing", desc: "Final quality checks, premium finishing, and careful packaging for dispatch.", icon: Truck, num: "06" },
];

export default function ProcessPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="process-page" className="pt-20 lg:pt-24">
      <SEO
        title="Our Process | From Yarn to Finished Fabric — KF Prints Textile Manufacturing"
        description="See how KF Prints transforms yarn into premium finished fabric: design, printing, processing, quality control and global dispatch — a 75-year refined manufacturing process in Surat, India."
        keywords="textile manufacturing process, fabric production steps, fabric printing process India, textile quality control"
        path="/process"
      />
      {/* Hero */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="section-divider mb-8" />
          <h1 className="text-5xl md:text-7xl font-serif tracking-tight text-foreground mb-6">Our Process</h1>
          <p className="text-base md:text-lg font-sans text-muted-foreground max-w-xl leading-relaxed">
            A streamlined, efficient process designed to deliver consistency and quality at scale.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-24 lg:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="space-y-0">
            {steps.map((step, index) => (
              <AnimatedDiv key={step.title}>
                <div className={`grid lg:grid-cols-12 gap-8 items-center py-12 lg:py-16 ${index !== steps.length - 1 ? 'border-b border-border' : ''}`}>
                  <div className="lg:col-span-1">
                    <span className="text-5xl font-serif text-[#C5A059]/30">{step.num}</span>
                  </div>
                  <div className="lg:col-span-1 flex lg:justify-center">
                    <div className="w-14 h-14 rounded-full bg-[#0F172A] flex items-center justify-center">
                      <step.icon size={22} className="text-[#C5A059]" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="lg:col-span-3">
                    <h3 className="text-2xl font-serif text-foreground">{step.title}</h3>
                  </div>
                  <div className="lg:col-span-7">
                    <p className="text-base font-sans text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </AnimatedDiv>
            ))}
          </div>
        </div>
      </section>

      {/* Visual */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-4">
            <div className="aspect-[3/4] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1522753071498-f3137a65aee3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB0ZXh0aWxlJTIwZmFjdG9yeSUyMG1hY2hpbmVyeSUyMGludGVyaW9yJTIwaGlnaCUyMHRlY2h8ZW58MHx8fHwxNzczNzQyOTk4fDA&ixlib=rb-4.1.0&q=85" alt="Machinery" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[3/4] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1660980041852-230420b8f99f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB0ZXh0aWxlJTIwZmFjdG9yeSUyMG1hY2hpbmVyeSUyMGludGVyaW9yJTIwaGlnaCUyMHRlY2h8ZW58MHx8fHwxNzczNzQyOTk4fDA&ixlib=rb-4.1.0&q=85" alt="Factory" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[3/4] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1739863305972-4eea3851e521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHx0ZXh0aWxlJTIwcHJpbnRpbmclMjBmYWN0b3J5JTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzM3NDMwODJ8MA&ixlib=rb-4.1.0&q=85" alt="Production" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
